import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import PackageOptions from './PackageOptions';
import styles from './styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';

const StyledToggleButtonGroup = (props) => (
    <ToggleButtonGroup
        {...props}
        sx={{
            '& .MuiToggleButtonGroup-grouped': {
                margin: 0,
                border: 0,
                flexWrap: 'wrap',
            },
            ...props.sx,
        }}
    />
);

const StyledToggleButton = (props) => (
    <ToggleButton
        {...props}
        sx={{
            padding: 0,
            textTransform: 'none',
            display: 'block',
            width: '180px',
            height: '180px',
            '& img': {
                border: '2px solid #e5e5e5',
                borderRadius: '7px',
            },
            '&:hover img': {
                color: 'rgb(26, 88, 159)',
                border: '2px solid #337AB7',
            },
            '&:focus img, &:focus-visible img': {
                color: 'rgb(26, 88, 159)',
                border: '2px solid #ff9800',
            },
            '&.Mui-selected img': {
                color: 'rgb(26, 88, 159)',
                border: '4px solid #337AB7',
                backgroundColor: '#337AB720',
            },
            ...props.sx,
        }}
    />
);

const theme = createTheme({
    palette: {
        action: {
            active: "#EEEEEE"
        },
        text: {
            primary: "#EEEEEE"
        }
    }
});

function AllPackageOptions(props) {
    const [packageSize, setSize] = React.useState(props.packageSize);
    const [packageOptionList, setPackageOptionList] = useState(props.packageOptionList);
    const [isLoading, setIsLoading] = useState(props.isLoading);
    const firstButtonRef = useRef(null);

    useEffect(() => {
        setPackageOptionList(props.packageOptionList);
        setIsLoading(props.isLoading);
    }, [props.packageOptionList, props.isLoading]);

    useEffect(() => {
        setSize(props.packageSize);
    }, [props.packageSize]);

    // Auto-focus first button when package options load
    useEffect(() => {
        if (!isLoading && packageOptionList.length > 0 && firstButtonRef.current) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                if (firstButtonRef.current) {
                    firstButtonRef.current.focus();
                }
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isLoading, packageOptionList.length]);

const handleSize = (event, newSize) => {
    setSize(newSize);
    props.onChange({
        target: {
            name: "packageSize",
            value: newSize,
        }
    });

    const selectedOption = packageOptionList.find(e => e.iconTitleText === newSize) || {};

    // Auto-fill the new package's pieces/weight unless the user has typed
    // a value (or the values came in from a saved quote and were preserved
    // as a manual edit). Don't also gate on existing values being > 0 —
    // a previous package's autofill itself sets weight > 0, which would
    // wrongly block the next package's autofill.
    if (!props.hasManualEdits && event) {
        props.onChange({
            target: {
                name: "pieces",
                value: selectedOption.fillPieces || 0
            }
        });

        props.onChange({
            target: {
                name: "weight",
                value: selectedOption.fillWeight || 0
            }
        });
    }

    props.onChange({
        target: {
            name: "packageDesc",
            value: selectedOption.iconTitleText
        }
    });

    props.onChange({
        target: {
            name: "packageImage",
            value: selectedOption.icon_url
        }
    });

    // After selection, focus the pieces input
    if (newSize) {
        setTimeout(() => {
            const piecesInput = document.querySelector('input[name="pieces"]');
            if (piecesInput) {
                piecesInput.focus();
                piecesInput.select();
            }
        }, 100);
    }
};

    const LoadingSkeletons = () => (
        <Grid container spacing={2} style={{ padding: '0 15px' }}>
            {[1, 2, 3, 4].map((n) => (
                <Grid item key={n} xs={12} sm={6} md={4} lg={3}>
                    <Box sx={{ width: 180, height: 220, mb: 4 }}>
                        <Skeleton 
                            variant="rounded" 
                            width={180} 
                            height={180} 
                            style={{ borderRadius: '7px' }}
                        />
                        <Skeleton 
                            variant="text" 
                            width={180} 
                            height={40}
                            style={{ marginTop: '8px' }}
                        />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );

    const renderContent = () => {
        if (isLoading) {
            return <LoadingSkeletons />;
        }

        if (!packageOptionList || packageOptionList.length === 0) {
            return (
                <Alert 
                    severity="error" 
                    style={{ marginBottom: '20px', width: '100%' }}
                >
                    No package types available. Please try again later.
                </Alert>
            );
        }

        return (
            <StyledToggleButtonGroup
                size="small"
                value={packageSize}
                exclusive
                onChange={handleSize}
                style={styles.root}
            >
                {packageOptionList.map((option, index) => (
                    <StyledToggleButton
                        key={option.iconTitleText}
                        value={option.iconTitleText}
                        disableRipple
                        style={{ marginBottom: '60px' }}
                        ref={index === 0 ? firstButtonRef : null}
                    >
                        <PackageOptions
                            packageType={option.iconTitleText}
                            image={option.icon_url}
                            alt={option.iconTitleText}
                            subText={option.iconSubText}
                        />
                    </StyledToggleButton>
                ))}
            </StyledToggleButtonGroup>
        );
    };

    return (
        <div>
            <div style={{ paddingLeft: '45px' }}>
                <ThemeProvider theme={theme}>
                    {renderContent()}
                </ThemeProvider>
            </div>
            <div style={{ paddingBottom: '20px' }} />
        </div>
    );
}

export default AllPackageOptions;